import { useState, useEffect } from 'react'
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, InputAdornment, MenuItem, Select,
  Stack, TextField, Typography,
} from '@mui/material'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Asset, AssetCategory, AssetCriticality, AssetStatus, ChecklistTemplateSummary, CreateAssetRequest, Site, Vendor } from '@cmms/types'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { useOrgSettings } from '../../../hooks/useOrgSettings'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import { TreeLocationPicker } from '../../../components/TreeLocationPicker'
import { SearchableSelect } from '../../../components/SearchableSelect'

interface Props {
  mode: 'create' | 'edit'
  asset?: Asset
  categories: AssetCategory[]
  sites: Site[]
  onClose: () => void
}

export function AssetFormDialog({ mode, asset, categories, sites, onClose }: Props) {
  const qc = useQueryClient()
  const isEdit = mode === 'edit'
  const navigate = useNavigate()
  const { prefs } = useOrgSettings()

  // Fetch vendors for the dropdown
  const vendorsQ = useQuery({ queryKey: ['vendors'], queryFn: () => api.vendors.list(), staleTime: 60_000 })
  const vendorsList: Vendor[] = Array.isArray(vendorsQ.data) ? vendorsQ.data : []

  // Fetch checklists
  const checklistsQ = useQuery({ queryKey: ['checklists', { status: 'published' }], queryFn: () => api.checklists.list({ status: 'published' }), staleTime: 60_000 })
  const allChecklists: ChecklistTemplateSummary[] = Array.isArray(checklistsQ.data) ? checklistsQ.data : []

  // Load existing linked checklists in edit mode
  const existingClQ = useQuery({ queryKey: ['asset-checklists', asset?.id], queryFn: () => api.assets.listChecklists(asset!.id), enabled: isEdit && !!asset?.id })
  const [checklistIds, setChecklistIds] = useState<string[]>([])
  useEffect(() => {
    if (existingClQ.data) setChecklistIds(existingClQ.data.map(c => c.template_id))
  }, [existingClQ.data])

  // ─── General ──────────────────────────────────────────────────
  const [name, setName]             = useState(asset?.name ?? '')
  const [desc, setDesc]             = useState(asset?.description ?? '')
  const [status, setStatus]         = useState<AssetStatus>(asset?.status ?? 'active')
  const [categoryId, setCategoryId] = useState(asset?.category_id ?? '')
  const [criticality, setCriticality] = useState<AssetCriticality>(asset?.criticality ?? 'normal')

  // ─── Location ─────────────────────────────────────────────────
  const [siteId, setSiteId]             = useState(asset?.site_id ?? '')
  const [locationId, setLocationId]     = useState(asset?.location_id ?? '')
  const [locationName, setLocationName] = useState(asset?.location_name ?? '')

  // ─── Specifications ───────────────────────────────────────────
  const [manufacturer, setManufacturer] = useState(asset?.manufacturer ?? '')
  const [model, setModel]               = useState(asset?.model ?? '')
  const [serial, setSerial]             = useState(asset?.serial_number ?? '')

  // ─── Lifecycle ────────────────────────────────────────────────
  const [purchaseDate, setPurchaseDate]   = useState(asset?.purchase_date ?? '')
  const [purchaseCost, setPurchaseCost]   = useState(asset?.purchase_cost != null ? String(asset.purchase_cost) : '')
  const [warrantyExpiry, setWarrantyExpiry] = useState(asset?.warranty_expiry ?? '')

  // ─── Maintenance (stored in metadata) ─────────────────────────
  const meta = (asset?.metadata ?? {}) as Record<string, any>
  const [lastMaintenance, setLastMaintenance]     = useState(meta.last_maintenance_date ?? '')
  const [nextMaintenance, setNextMaintenance]     = useState(meta.next_maintenance_date ?? '')
  const storedInterval = meta.maintenance_interval ?? ''
  const isPreset = ['7', '14', '30', '60', '90', '180', '365'].includes(String(storedInterval))
  const [intervalType, setIntervalType] = useState<string>(isPreset ? String(storedInterval) : storedInterval ? 'custom' : '')
  const [customDays, setCustomDays] = useState(isPreset ? '' : String(storedInterval).replace(/\D/g, ''))
  const [manufacturedDate, setManufacturedDate]   = useState(meta.manufactured_date ?? '')
  const [vendorId, setVendorId]                     = useState(meta.vendor_id ?? '')
  // Only relevant at create time — controls whether the backend spawns
  // the first preventive maintenance work order immediately, or waits.
  const [skipFirstPm, setSkipFirstPm] = useState<boolean>(false)

  // Auto-calculate next maintenance date when interval or last serviced changes
  const resolvedDays = intervalType === 'custom' ? parseInt(customDays || '0') : parseInt(intervalType || '0')
  useEffect(() => {
    if (resolvedDays > 0 && lastMaintenance) {
      const base = new Date(lastMaintenance)
      base.setDate(base.getDate() + resolvedDays)
      setNextMaintenance(base.toISOString().split('T')[0]!)
    } else if (resolvedDays > 0 && !lastMaintenance) {
      // If no last maintenance, calculate from today
      const today = new Date()
      today.setDate(today.getDate() + resolvedDays)
      setNextMaintenance(today.toISOString().split('T')[0]!)
    }
  }, [resolvedDays, lastMaintenance])

  // ─── Notes ────────────────────────────────────────────────────
  const [notes, setNotes] = useState(asset?.notes ?? '')

  function buildBody() {
    const metadata: Record<string, any> = { ...(asset?.metadata ?? {}) }
    if (lastMaintenance) metadata.last_maintenance_date = lastMaintenance; else delete metadata.last_maintenance_date
    if (nextMaintenance) metadata.next_maintenance_date = nextMaintenance; else delete metadata.next_maintenance_date
    const resolvedInterval = intervalType === 'custom' ? customDays : intervalType
    if (resolvedInterval) metadata.maintenance_interval = resolvedInterval; else delete metadata.maintenance_interval
    // Only emit pm_skip_first_wo at create time; on edit it's noise.
    if (!isEdit && resolvedInterval && skipFirstPm) metadata.pm_skip_first_wo = true
    else delete metadata.pm_skip_first_wo
    if (manufacturedDate) metadata.manufactured_date = manufacturedDate; else delete metadata.manufactured_date
    if (vendorId) {
      metadata.vendor_id = vendorId
      // Also store vendor name for display when vendor list isn't loaded
      const v = vendorsList.find((v) => v.id === vendorId)
      if (v) metadata.vendor_name = v.name
    } else {
      delete metadata.vendor_id
      delete metadata.vendor_name
    }

    // On create, an empty field should simply be omitted (`undefined`) so
    // the backend applies its own defaults/nulls. On edit, omitting a
    // field means "leave it as-is" (sparse update) — so a field the user
    // just cleared must be sent as an explicit `null`, or the old value
    // silently survives the "successful" save.
    const empty = isEdit ? null : undefined

    return {
      name: name.trim(),
      description: desc.trim() || empty,
      status,
      criticality,
      category_id: categoryId || empty,
      site_id: siteId || empty,
      location_id: locationId || empty,
      manufacturer: manufacturer.trim() || empty,
      model: model.trim() || empty,
      serial_number: serial.trim() || empty,
      purchase_date: purchaseDate || empty,
      purchase_cost: purchaseCost ? parseFloat(purchaseCost) : empty,
      warranty_expiry: warrantyExpiry || empty,
      notes: notes.trim() || empty,
      // Same reasoning as above — on edit, always send `metadata` (even
      // `{}`) so clearing every maintenance field actually clears the
      // stored JSONB blob instead of leaving stale keys behind.
      metadata: Object.keys(metadata).length > 0 ? metadata : (isEdit ? {} : undefined),
    }
  }

  const createM = useMutation({
    mutationFn: async () => {
      // buildBody() returns nullable fields for edit-mode clearing; when
      // !isEdit (this branch), `empty` is always `undefined`, so the
      // result genuinely satisfies CreateAssetRequest at runtime.
      const created = await api.assets.create(buildBody() as unknown as CreateAssetRequest)
      for (const tid of checklistIds) {
        await api.assets.addChecklist(created.id, tid).catch(() => {})
      }
      return created
    },
    onSuccess: () => { toast.success('Asset created'); qc.invalidateQueries({ queryKey: ['assets'] }); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const updateM = useMutation({
    mutationFn: async () => {
      const updated = await api.assets.update(asset!.id, buildBody())
      // Sync checklists: remove old, add new
      const existing = (existingClQ.data ?? []).map(c => c.template_id)
      for (const tid of existing) {
        if (!checklistIds.includes(tid)) await api.assets.removeChecklist(asset!.id, tid).catch(() => {})
      }
      for (const tid of checklistIds) {
        if (!existing.includes(tid)) await api.assets.addChecklist(asset!.id, tid).catch(() => {})
      }
      return updated
    },
    onSuccess: () => { toast.success('Asset updated'); qc.invalidateQueries({ queryKey: ['assets'] }); qc.invalidateQueries({ queryKey: ['asset-checklists', asset!.id] }); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const isPending = createM.isPending || updateM.isPending

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        {isEdit ? `Edit ${asset?.name}` : 'New asset'}
      </DialogTitle>
      <DialogContent>
        {!isEdit && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
            Add equipment to the register. An asset code will be auto-generated.
          </Typography>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* ─── Left column ─── */}
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <SectionLabel>General</SectionLabel>
            <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus placeholder="e.g. HVAC Unit A-101" />
            <TextField label="Description" value={desc} onChange={(e) => setDesc(e.target.value)} size="small" fullWidth multiline minRows={2} placeholder="What is this asset?" />
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
                  Status
                </Typography>
                <Select value={status} onChange={(e) => setStatus(e.target.value as AssetStatus)} size="small" fullWidth>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="under_maintenance">Under maintenance</MenuItem>
                  <MenuItem value="decommissioned">Decommissioned</MenuItem>
                </Select>
                <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: 0.5 }}>
                  Set manually — work orders don't change this automatically.
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <SearchableSelect
                  value={(categories ?? []).some((c) => c.id === categoryId) ? categoryId : ''}
                  onChange={setCategoryId}
                  options={categories ?? []}
                  getValue={(c) => c.id}
                  getLabel={(c) => c.name}
                  placeholder="Select category"
                  renderOption={(c) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {c.color && <Box component="span" sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: c.color, display: 'inline-block' }} />}
                      <Typography sx={{ fontSize: 13 }}>{c.name}</Typography>
                    </Stack>
                  )}
                />
              </Box>
            </Stack>
            <Box>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
                Criticality
              </Typography>
              <Select value={criticality} onChange={(e) => setCriticality(e.target.value as AssetCriticality)} size="small" fullWidth>
                <MenuItem value="critical">
                  <Box component="span" sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#DC2626', mr: 1, display: 'inline-block' }} />
                  Critical
                </MenuItem>
                <MenuItem value="important">
                  <Box component="span" sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#D97706', mr: 1, display: 'inline-block' }} />
                  Important
                </MenuItem>
                <MenuItem value="normal">
                  <Box component="span" sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#059669', mr: 1, display: 'inline-block' }} />
                  Normal
                </MenuItem>
              </Select>
            </Box>

            <Divider />
            <SectionLabel>Location</SectionLabel>
            <SearchableSelect
              value={(sites ?? []).some((s) => s.id === siteId) ? siteId : ''}
              onChange={(sid) => { setSiteId(sid); setLocationId(''); setLocationName('') }}
              options={sites ?? []}
              getValue={(s) => s.id}
              getLabel={(s) => s.name}
              placeholder="Select site *"
            />
            {siteId && (
              <TreeLocationPicker siteId={siteId} value={locationId} displayName={locationName || undefined}
                onChange={(id, n) => { setLocationId(id); setLocationName(n) }}
                onClear={() => { setLocationId(''); setLocationName('') }} />
            )}

            <Divider />
            <SectionLabel>Notes</SectionLabel>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} size="small" fullWidth multiline minRows={3} placeholder="Any additional notes…" />
          </Stack>

          {/* ─── Right column ─── */}
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <SectionLabel>Specifications</SectionLabel>
            <TextField label="Manufacturer" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} size="small" fullWidth />
            <TextField label="Model" value={model} onChange={(e) => setModel(e.target.value)} size="small" fullWidth />
            <TextField label="Serial number" value={serial} onChange={(e) => setSerial(e.target.value)} size="small" fullWidth />
            <TextField label="Manufactured date" type="date" value={manufacturedDate} onChange={(e) => setManufacturedDate(e.target.value)}
              size="small" fullWidth InputLabelProps={{ shrink: true }} />

            <Divider />
            <SectionLabel>Lifecycle & costs</SectionLabel>
            <Stack direction="row" spacing={2}>
              <TextField label="Purchase date" type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)}
                size="small" fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Purchase cost" type="number" value={purchaseCost} onChange={(e) => setPurchaseCost(e.target.value)}
                size="small" fullWidth InputProps={{ startAdornment: <InputAdornment position="start">{prefs.currencySymbol}</InputAdornment> }} />
            </Stack>
            <TextField label="Warranty expires" type="date" value={warrantyExpiry} onChange={(e) => setWarrantyExpiry(e.target.value)}
              size="small" fullWidth InputLabelProps={{ shrink: true }} />

            <Divider />
            <SectionLabel>Maintenance</SectionLabel>
            <Stack direction="row" spacing={2}>
              <TextField label="Last serviced" type="date" value={lastMaintenance} onChange={(e) => setLastMaintenance(e.target.value)}
                size="small" fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Next due" type="date" value={nextMaintenance} onChange={(e) => setNextMaintenance(e.target.value)}
                size="small" fullWidth InputLabelProps={{ shrink: true }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
                  Maintenance interval
                </Typography>
                <Select value={intervalType} onChange={(e) => { setIntervalType(e.target.value); if (e.target.value !== 'custom') setCustomDays('') }}
                  displayEmpty size="small" fullWidth>
                  <MenuItem value="">No interval</MenuItem>
                  <MenuItem value="7">Weekly (7 days)</MenuItem>
                  <MenuItem value="14">Bi-weekly (14 days)</MenuItem>
                  <MenuItem value="30">Monthly (30 days)</MenuItem>
                  <MenuItem value="60">Bi-monthly (60 days)</MenuItem>
                  <MenuItem value="90">Quarterly (90 days)</MenuItem>
                  <MenuItem value="180">Semi-annual (180 days)</MenuItem>
                  <MenuItem value="365">Annual (365 days)</MenuItem>
                  <MenuItem value="custom">Custom…</MenuItem>
                </Select>
              </Box>
              {intervalType === 'custom' && (
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
                    Custom (days)
                  </Typography>
                  <TextField value={customDays} onChange={(e) => setCustomDays(e.target.value.replace(/\D/g, ''))}
                    size="small" fullWidth type="number" placeholder="e.g. 45"
                    InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }} />
                </Box>
              )}
            </Stack>
            {!isEdit && resolvedDays > 0 && (
              <Box sx={{ mt: 0.5, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={skipFirstPm} onChange={(e) => setSkipFirstPm(e.target.checked)} />}
                  label={
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Don't schedule the first work order yet</Typography>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
                        Skip the initial preventive WO. The chain begins after you complete the first maintenance manually.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', m: 0, '& .MuiFormControlLabel-label': { mt: 0.25 } }}
                />
              </Box>
            )}

            <Divider />
            <SectionLabel>Vendor / supplier</SectionLabel>
            <SearchableSelect
              value={vendorsList.some((v) => v.id === vendorId) ? vendorId : ''}
              onChange={setVendorId}
              options={vendorsList}
              getValue={(v) => v.id}
              getLabel={(v) => `${v.name}${v.category ? ` — ${v.category}` : ''}`}
              placeholder="Select vendor"
            />
            {vendorsList.length === 0 && !vendorsQ.isLoading && (
              <Button size="small" onClick={() => { onClose(); navigate('/vendors') }}
                sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', alignSelf: 'flex-start' }}>
                + Create a vendor first →
              </Button>
            )}

            <Divider />
            <SectionLabel>Linked checklists</SectionLabel>
            <SearchableSelect
              multiple
              value={checklistIds}
              onChange={setChecklistIds}
              options={allChecklists}
              getValue={(c) => c.id}
              getLabel={(c) => c.name}
              placeholder="Select checklists"
              renderOption={(c) => (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                  <ChecklistOutlinedIcon sx={{ fontSize: 16, color: checklistIds.includes(c.id) ? '#8B5CF6' : 'text.secondary' }} />
                  <Typography sx={{ fontSize: 13, flex: 1 }}>{c.name}</Typography>
                  {checklistIds.includes(c.id) && <CheckCircleOutlinedIcon sx={{ fontSize: 16, color: '#059669' }} />}
                </Stack>
              )}
            />
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
              These checklists will auto-attach when this asset is used in a work order.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || !siteId || isPending}
          onClick={() => isEdit ? updateM.mutate() : createM.mutate()}
          sx={{ fontSize: 13, fontWeight: 600 }}>
          {isPending ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create asset')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
      {children}
    </Typography>
  )
}
